import {
	BackgroundAttributes,
	BackgroundControl,
	SpacingAttributes,
	SpacingControl,
	TypographyAttributes,
	TypographyControl,
} from '@redfox-thank-you/control-groups';
import { BaseControl } from '@redfox-thank-you/controls';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, SelectControl, ToggleControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import React from 'react';

import type { OrderInfoType, SettingsProps } from './types';

/**
 * Order Info block settings component (Inspector Controls).
 */
export const Settings: React.FC< SettingsProps > = ( {
	updateAttributes,
	getAttribute,
} ) => {
	// Helper functions for specific attribute types
	const updateTypography = (
		section: 'label' | 'blockText',
		typographyAttributes: Partial< TypographyAttributes >,
	) => {
		const stylePath =
			section === 'blockText' ? 'style.block.text' : `style.${ section }`;
		updateAttributes( stylePath, typographyAttributes );
	};

	const updateBackground = (
		backgroundAttributes: Partial< BackgroundAttributes >,
	) => {
		updateAttributes( 'style.block.background', backgroundAttributes );
	};

	const updateSpacing = (
		spacingAttributes: Partial< SpacingAttributes >,
	) => {
		updateAttributes( 'style.block.spacing', spacingAttributes );
	};

	// Extract style properties with defaults using getAttribute
	const blockTextStyle = getAttribute< TypographyAttributes >(
		'style.block.text',
		{},
	);
	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
		{},
	);
	const blockSpacingStyle = getAttribute< SpacingAttributes >(
		'style.block.spacing',
		{},
	);
	const labelStyle = getAttribute< TypographyAttributes >(
		'style.label',
		{},
	);

	// Extract settings properties with defaults using getAttribute
	const infoType = getAttribute< OrderInfoType >(
		'settings.infoType',
		'order_number',
	);
	const showLabel = getAttribute< boolean >( 'settings.label.show', true );

	// Info type options
	const infoTypes = [
		{
			label: __( 'Order Number', 'redfox-thank-you' ),
			value: 'order_number',
		},
		{
			label: __( 'Order Date', 'redfox-thank-you' ),
			value: 'order_date',
		},
		{
			label: __( 'Total Amount', 'redfox-thank-you' ),
			value: 'total_amount',
		},
		{
			label: __( 'Payment Method', 'redfox-thank-you' ),
			value: 'payment_method',
		},
		{
			label: __( 'Order Status', 'redfox-thank-you' ),
			value: 'order_status',
		},
		{
			label: __( 'Subtotal', 'redfox-thank-you' ),
			value: 'subtotal',
		},
		{
			label: __( 'Shipping', 'redfox-thank-you' ),
			value: 'shipping',
		},
		{
			label: __( 'Tax', 'redfox-thank-you' ),
			value: 'tax',
		},
		{
			label: __( 'Discount', 'redfox-thank-you' ),
			value: 'discount',
		},
	];

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Settings', 'redfox-thank-you' ) }
					initialOpen={ true }
				>
					<SelectControl
						label={ __( 'Information Type', 'redfox-thank-you' ) }
						value={ infoType }
						options={ infoTypes }
						onChange={ ( value: string ) =>
							updateAttributes(
								'settings.infoType',
								value as OrderInfoType,
							)
						}
						help={ __(
							'Choose which order information to display.',
							'redfox-thank-you',
						) }
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Label Settings', 'redfox-thank-you' ) }
					initialOpen={ false }
				>
					<BaseControl
						id="show-label"
						label={ __( 'Show Label', 'redfox-thank-you' ) }
						help={ __(
							'Display a label for this information.',
							'redfox-thank-you',
						) }
						inline
						helpAsTooltip
					>
						<ToggleControl
							label=""
							checked={ showLabel }
							onChange={ ( value ) =>
								updateAttributes( 'settings.label.show', value )
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
				</PanelBody>
			</InspectorControls>

			<InspectorControls group="styles">
				<TypographyControl
					title={ __( 'Text', 'redfox-thank-you' ) }
					initialOpen={ true }
					attributes={ blockTextStyle }
					setAttributes={ ( attrs ) =>
						updateTypography( 'blockText', attrs )
					}
				/>

				{ showLabel && (
					<TypographyControl
						title={ __( 'Label Text', 'redfox-thank-you' ) }
						initialOpen={ false }
						attributes={ labelStyle }
						setAttributes={ ( attrs ) =>
							updateTypography( 'label', attrs )
						}
						enabledControls={ {
							textAlign: false,
						} }
					/>
				) }

				<BackgroundControl
					title={ __( 'Background', 'redfox-thank-you' ) }
					initialOpen={ false }
					responsive={ true }
					attributes={ blockBackgroundStyle }
					setAttributes={ updateBackground }
					enabledControls={ {
						backgroundColor: true,
						backgroundImage: true,
						backgroundGradient: true,
						backgroundPattern: false,
						backgroundOverlay: true,
						advancedOptions: true,
						responsive: true,
					} }
				/>

				<SpacingControl
					title={ __( 'Spacing', 'redfox-thank-you' ) }
					initialOpen={ false }
					attributes={ blockSpacingStyle }
					setAttributes={ updateSpacing }
					enabledControls={ {
						padding: true,
						margin: true,
						responsive: true,
						presets: true,
						boxModel: true,
						autoValues: true,
					} }
					units={ [ 'px', 'em', 'rem', '%' ] }
					min={ 0 }
					max={ 200 }
					allowReset={ true }
				/>
			</InspectorControls>
		</>
	);
};
